import React, { useState, useRef, useEffect } from 'react';
import OrderDesign from './OrderDesign';
import { OrderCardBubbles } from './components/OrderCardBubbles';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import './StrategyPreferenceRewardModal.css';

function StrategyPreferenceRewardModal({ isOpen, onClose, grantedOrders }) {
  const [revealedRewards, setRevealedRewards] = useState([]);
  const [enrichedRewards, setEnrichedRewards] = useState([]);
  const videoRefs = useRef([]);
  const didPlayHiddenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      didPlayHiddenRef.current = false;
      return;
    }
    if (isOpen && grantedOrders && grantedOrders.length > 0) {
      // Reset state when modal opens
      setRevealedRewards(new Array(grantedOrders.length).fill(false));
      setEnrichedRewards(new Array(grantedOrders.length).fill(null));
      if (!didPlayHiddenRef.current) {
        playSoundEffect('/sounds/hidden_rewards.mp3');
        didPlayHiddenRef.current = true;
      }
    }
  }, [isOpen, grantedOrders]);

  if (!isOpen || !grantedOrders || grantedOrders.length === 0) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content strategy-preference-reward-modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ color: '#fff', textAlign: 'center', marginBottom: '20px' }}>
          Your Strategy Orders
        </h2>
        <p style={{ color: '#fff', textAlign: 'center', marginBottom: '30px', fontSize: '16px' }}>
          Click cards to reveal your rewards!
        </p>
        <div 
          className="rewards-grid-container"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '40px',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: 'calc(90vh - 200px)', // Leave space for header, text, and button
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '20px',
            width: '100%',
            maxWidth: '860px', // 2 cards (390px each) + gap (40px) + padding
            margin: '0 auto',
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
          }}
        >
          {grantedOrders.map((order, index) => {
            const isRevealed = revealedRewards[index];
            const enrichedOrder = enrichedRewards[index];
            const rarity = order.rarity?.toLowerCase() || 'common';
            
            return (
              <div
                key={order.id || index}
                onClick={async () => {
                  if (!isRevealed) {
                    setRevealedRewards(prev => {
                      const newState = [...prev];
                      newState[index] = true;
                      return newState;
                    });
                    const isLegendary = (order.rarity || '').toLowerCase() === 'legendary';
                    playSoundEffect(isLegendary ? '/sounds/legendary_reveal.mp3' : '/sounds/order_reveal.mp3');
                    
                    // Fetch full order data (includes strategems, drones, weapons)
                    if (order.id) {
                      const token = localStorage.getItem('token');
                      try {
                        const res = await fetch(`${API_URL}/order/${order.id}/full`, {
                          headers: {
                            Authorization: `Bearer ${token}`
                          }
                        });
                        if (res.ok) {
                          const fullOrder = await res.json();
                          // Preserve rarity from the original order data
                          fullOrder.rarity = order.rarity || fullOrder.rarity;
                          setEnrichedRewards(prev => {
                            const newState = [...prev];
                            newState[index] = fullOrder;
                            return newState;
                          });
                        } else {
                          console.error('Failed to fetch full order data:', res.status);
                        }
                      } catch (err) {
                        console.error('Failed to fetch full order data:', err);
                      }
                    }
                  }
                }}
                style={{
                  width: '390px',
                  height: '390px',
                  margin: 0,
                  padding: 0,
                  perspective: '1000px',
                  cursor: !isRevealed ? 'pointer' : 'default',
                  position: 'relative',
                  overflow: 'visible',
                  flexShrink: 0
                }}
              >
                <div
                  style={{
                    width: '390px',
                    height: '390px',
                    position: 'relative',
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.6s',
                    transform: isRevealed ? 'rotateY(180deg)' : 'rotateY(0deg)'
                  }}
                >
                  {/* Front side - Question mark with video */}
                  <div style={{
                    position: 'absolute',
                    width: '390px',
                    height: '390px',
                    left: '50%',
                    transform: 'translateX(-50%) rotateY(0deg)',
                    padding: '20px 5px',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    border: '2px solid #C0C0C0',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    overflow: 'hidden',
                    boxShadow: '0 0 8px rgba(200, 200, 200, 0.6), inset 0 0 10px rgba(255, 255, 255, 0.1)'
                  }}>
                    <video
                      ref={el => { videoRefs.current[index] = el; }}
                      poster="/images/reward_intel.jpg"
                      autoPlay
                      loop
                      muted
                      playsInline
                      style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        pointerEvents: 'none',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)'
                      }}
                      onCanPlay={() => {
                        const video = videoRefs.current[index];
                        if (video && !isRevealed) {
                          video.play().catch(err => {
                            console.error(`Error playing video ${index}:`, err);
                          });
                        }
                      }}
                    >
                      <source src="/images/reward_animation.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  
                  {/* Back side - Reward content */}
                  {isRevealed && (
                    <div style={{
                      position: 'absolute',
                      width: '390px',
                      height: '390px',
                      left: '50%',
                      transform: 'translateX(-50%) rotateY(180deg)',
                      padding: enrichedOrder ? '0' : '20px 5px',
                      backgroundColor: enrichedOrder ? 'transparent' : 'rgba(0, 0, 0, 0.8)',
                      border: 'none',
                      borderRadius: enrichedOrder ? '0' : '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      boxSizing: 'border-box',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      overflow: 'visible'
                    }}>
                      {enrichedOrder ? (
                        <div style={{ 
                          width: '100%', 
                          height: '100%', 
                          position: 'relative',
                          overflow: 'visible',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          margin: 0,
                          padding: 0
                        }}>
                          <div className={`reward-order-container rarity-${rarity}`} style={{
                            width: '390px',
                            height: '390px',
                            position: 'relative',
                            margin: 0,
                            padding: 0
                          }}>
                            <OrderDesign
                              order={enrichedOrder}
                              minimal={false}
                              noMargin={true}
                              gameState={null}
                            />
                            <OrderCardBubbles rarity={enrichedOrder?.rarity || rarity || 'common'} />
                          </div>
                        </div>
                      ) : (
                        <div style={{ color: '#fff', textAlign: 'center' }}>
                          <p>{order.name || 'Order'}</p>
                          <p style={{ fontSize: '14px', color: '#aaa' }}>{rarity}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ 
          textAlign: 'center', 
          marginTop: '20px',
          flexShrink: 0,
          paddingTop: '10px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 30px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            Claim all and close
          </button>
        </div>
      </div>
    </div>
  );
}

export default StrategyPreferenceRewardModal;
